import React, { FC, useContext } from 'react';
import calculateLineItemQuantity from '../../../helpers/calculate-line-item-quantity';
import useIsBreakpoint from '../../../hooks/use-is-breakpoint/use-is-breakpoint';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import BasketFeaturesType from '../../../models/basket-features-type';
import { BasketTableLabelsType } from '../../../models/basket-table-labels-type';
import BreakpointEnum from '../../../models/breakpoint-enum';
import { CheckoutBasketTableLabelsType } from '../../../models/checkout-basket-table-labels-type';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import { ProductBasketLineItemType } from '../../../models/product-basket-line-item-type';
import { ProductLineItemType } from '../../../models/product-line-item-type';
import ProductTypeEnum from '../../../models/product-type-enum';
import TableHeading from '../../atoms/table-heading/table-heading';
import close from '../../providers/overlay/close.overlay.actions';
import open from '../../providers/overlay/open.overlay.actions';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
import BasketLeadTimes from '../basket-lead-times/basket-lead-times';
// eslint-disable-next-line import/no-cycle
import BasketRecommendedAlternatives from '../basket-recommended-alternatives/basket-recommended-alternatives';
import BasketTableRow from '../basket-table-row/basket-table-row';

type LineItemType = ProductLineItemType | ProductBasketLineItemType;

interface Props {
  readonly basketLineItems?: ProductBasketLineItemType[];
  readonly checkoutLabels?: CheckoutBasketTableLabelsType;
  readonly confirmedBackOrderSkus?: string[];
  readonly features: BasketFeaturesType;
  readonly labels: BasketTableLabelsType;
  readonly lastAddedVariantSkus?: string[];
  readonly lineItems?: ProductLineItemType[];
  readonly onLineAdd?: (item: LineItemType, quantity: number) => void;
  readonly onLineRemove?: (item: LineItemType) => void;
  readonly onLineReplace?: (
    item: LineItemType,
    quantity: number,
    replacesItem?: LineItemType
  ) => void;
  readonly onLineItemChangeQuantity?: (
    item: LineItemType,
    quantity: number
  ) => void;
  readonly onLineUpdateQuantity?: (
    item: LineItemType,
    quantity: number
  ) => void;
  readonly onToggleConfirmedBackOrderSku?: (sku: string) => void;
}

const BasketTable: FC<Props> = ({
  basketLineItems,
  checkoutLabels,
  confirmedBackOrderSkus,
  features,
  labels,
  lastAddedVariantSkus,
  lineItems,
  onLineAdd,
  onLineRemove,
  onLineReplace,
  onLineItemChangeQuantity,
  onLineUpdateQuantity,
  onToggleConfirmedBackOrderSku,
}) => {
  const overlayDispatch = useContext(OverlayDispatchContext);

  const {
    discountColumnLabel,
    priceColumnLabel,
    productDetailsColumnLabel,
    quantityColumnLabel,
    stockColumnLabel,
    subTotalColumnLabel,
  } = labels;

  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const breakpoint = useIsBreakpoint();

  const items: ProductLineItemType[] = lineItems || basketLineItems;

  const buttonCount =
    Number(features.addButton) +
    Number(features.replaceButton) +
    Number(features.removeButton);

  const buttonColumnWidthClass = ['w-0', 'w-24', 'w-44', 'w-64'][buttonCount];

  const openLeadTimesModal = (
    item: ProductBasketLineItemType,
    quantity: number
  ): void => {
    overlayDispatch(
      open(
        <BasketLeadTimes
          lineItem={item}
          checkoutLabels={checkoutLabels}
          requiredQuantity={quantity}
        />
      )
    );

    pushToDataLayer(GtmEventNameEnum.BasketLeadTimes, {
      ecommerce: {
        detail: {
          products: [
            {
              dimension3: item.product.variantSku,
              dimension4: item.quantityInStock ? 'In Stock' : 'Out of Stock',
              metric2: item.quantityInStock,
              price: item.listPriceFormatted,
              quantity: calculateLineItemQuantity(item).total,
            },
          ],
        },
      },
      productType: ProductTypeEnum.Website,
    });
  };

  const openRecommendedAlternativesModal = (
    item: ProductBasketLineItemType
  ): void => {
    overlayDispatch(
      open(
        <BasketRecommendedAlternatives
          lineItems={[item.reccomendedAlternative]}
          labels={labels}
          checkoutLabels={checkoutLabels}
          onLineAdd={(newItem, quantity) => {
            overlayDispatch(close());
            onLineAdd(newItem, quantity);

            pushToDataLayer(GtmEventNameEnum.BasketAlternativeAdd, {
              ecommerce: {
                add: {
                  products: [
                    {
                      dimension3: newItem.product.variantSku,
                      price: newItem.listPriceFormatted,
                    },
                  ],
                },
              },
              productType: ProductTypeEnum.Website,
            });
          }}
          onLineReplace={(replacementItem, quantity) => {
            overlayDispatch(close());
            onLineReplace(replacementItem, quantity, item);

            pushToDataLayer(GtmEventNameEnum.BasketAlternativeReplace, {
              ecommerce: {
                add: {
                  products: [
                    {
                      dimension3: replacementItem.product.variantSku,
                      price: replacementItem.listPriceFormatted,
                    },
                  ],
                },
              },
              productType: ProductTypeEnum.Website,
            });
          }}
        />
      )
    );

    pushToDataLayer(GtmEventNameEnum.BasketAlternative, {
      ecommerce: {
        detail: {
          products: [
            {
              dimension3: item.reccomendedAlternative.product.variantSku,
              price: item.reccomendedAlternative.listPriceFormatted,
            },
          ],
        },
      },
      productType: ProductTypeEnum.Website,
    });
  };

  return (
    <table
      className="border-separate table-fixed w-full"
      style={{ borderSpacing: 0 }}
    >
      <thead className="hidden lg:table-header-group">
        <tr>
          <TableHeading className="w-20" label="" />
          <TableHeading label={productDetailsColumnLabel} />
          {features.inStockColumn && (
            <TableHeading className="w-42" label={stockColumnLabel} />
          )}
          {features.discountColumn && (
            <TableHeading
              className="w-16 xl:w-18"
              label={discountColumnLabel}
            />
          )}
          <TableHeading className="w-18 xl:w-20" label={priceColumnLabel} />
          <TableHeading
            className={
              features.subtotalColumn ? `w-20 xl:w-22` : 'w-26 xl:w-28'
            }
            label={quantityColumnLabel}
          />
          {features.subtotalColumn && (
            <TableHeading className="w-28" label={subTotalColumnLabel} />
          )}
          {buttonCount > 0 &&
            !(
              features.favouritesLayout && breakpoint === BreakpointEnum.lg
            ) && <TableHeading className={buttonColumnWidthClass} label="" />}
        </tr>
      </thead>

      <tbody>
        {items.map((item: ProductBasketLineItemType) => (
          <BasketTableRow
            key={item.product.guid}
            labels={labels}
            checkoutLabels={checkoutLabels}
            highlighted={
              !!lastAddedVariantSkus.find(
                (sku) => item.product.variantSku === sku
              )
            }
            lineItem={item}
            confirmedBackOrder={confirmedBackOrderSkus.includes(
              item.product.variantSku
            )}
            onConfirmedBackOrderToggle={() =>
              onToggleConfirmedBackOrderSku?.(item.product.variantSku)
            }
            onRemove={() => onLineRemove?.(item)}
            onReplace={(quantity) => onLineReplace?.(item, quantity)}
            onAdd={(quantity) => onLineAdd?.(item, quantity)}
            onChangeQuantity={(quantity) =>
              onLineItemChangeQuantity?.(item, quantity)
            }
            onUpdateQuantity={(quantity) =>
              onLineUpdateQuantity?.(item, quantity)
            }
            onCheckLeadTimes={(quantity) =>
              openLeadTimesModal(item as ProductBasketLineItemType, quantity)
            }
            onCheckRecommendedAlternatives={() =>
              openRecommendedAlternativesModal(
                item as ProductBasketLineItemType
              )
            }
            features={features}
          />
        ))}
      </tbody>
    </table>
  );
};

BasketTable.defaultProps = {
  basketLineItems: null,
  checkoutLabels: null,
  confirmedBackOrderSkus: [],
  lastAddedVariantSkus: [],
  lineItems: null,
  onLineAdd: null,
  onLineItemChangeQuantity: null,
  onLineRemove: null,
  onLineReplace: null,
  onLineUpdateQuantity: null,
  onToggleConfirmedBackOrderSku: null,
};

export default BasketTable;
